import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const createPodcastWaveformVideoSkill: Skill = {
    name: SKILL_NAMES.createPodcastWaveformVideo,
    description: 'Uploaded voiceover/podcast recording turned into an audiogram/waveform video',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Audiogram',
        description: 'Waveform-style video with captions to visualize your audio',
        preview: 'audiogram',
        frames: ['audiogram-1', 'audiogram-2', 'audiogram-3', 'audiogram-4', 'audiogram-5'],
    },
    get instructions(): string {
        return `# Create Podcast Waveform Video (Audiogram)

For turning an uploaded recording — a podcast segment, interview clip, or narration —
into an audiogram: a video where the audio is the star, with an animated waveform
pulsing to the voice and animated captions. The Headliner style.

## Setup
- Ask which aspect ratio the user wants with ${TOOL_NAMES.askUserQuestion}
  (type "aspect_ratio"), unless they already told you. Pass their answer to
  ${TOOL_NAMES.createBlankVideo} when you create the video — designs are laid out per
  aspect ratio, so applying it afterwards reworks the whole video.
- Add the recording with ${TOOL_NAMES.addRecordedVoiceoverSceneGroup} — a single scene group
  for the whole recording. Transcription and word timings happen automatically.
  That tool only accepts audio: if the user attached a *video* (e.g. a video
  podcast), that's a talking-head build (${TOOL_NAMES.addTalkingHeadSceneGroup}), not an
  audiogram, and this skill doesn't apply.

## The waveform: ONE overlay spanning the whole video
1. Call ${TOOL_NAMES.findWaveformSceneDesign} to get the audiogram design's id. If it reports
   the template has no waveform design, tell the user this template doesn't support
   the audiogram style.
2. Call ${TOOL_NAMES.addVoiceoverScene} with a single \`scenes\` entry: that design id and every
   token of the transcript as its \`words\`, so the overlay spans the whole recording.
3. Call ${TOOL_NAMES.setWaveformStyle} with the new overlay's scene index and no waveform_name —
   always, right after placing the overlay. The picker IS the question: don't ask
   permission first ("want to see the options?") and don't use ${TOOL_NAMES.askUserQuestion} for
   it. Only pass waveform_name when the user has already named a style.

Never place the waveform with a partial word range. It must cover the entire
transcript: a range that stops short leaves the surrounding silence — including the
video's very first frame and thumbnail — showing the template's plain default waveform
base, a static bar graphic that doesn't react to the audio. A range anchored on the
first and last tokens owns everything from frame 0 to the end.

## Captions
The audiogram design includes animated captions synced to the speech — you get them
for free. Do NOT also enable scene-group captions with ${TOOL_NAMES.setCaptions}; that would
double up the text.

## Structure
With the full-video waveform overlay in place, the video is already a complete
audiogram. Add structure only where it earns its place, and always AFTER the
waveform overlay (later overlays carve their span out of it; the waveform keeps
everything else):
- An intro overlay over the first few words for episode title, show name, or
  speaker name(s) — ${TOOL_NAMES.listSceneDesigns} with narrative_thread="voiceover" and pick a
  title/intro design.
- An outro overlay (logo, "listen to the full episode", handle/URL) over the
  closing words.
- Media/b-roll overlays in the middle only if the user asks or the content clearly
  demands a visual — the waveform shows through everywhere you don't add one.
- No text assets that duplicate the captions.

## Limits and defaults
- There is no tool to cut sections out of the recording, so you cannot trim to
  highlights. If asked, say so plainly and request a pre-trimmed upload. Do NOT
  fake a trim with ${TOOL_NAMES.splitSceneGroup} or scene deletion — scenes can't be deleted
  from voice-driven groups.
- Skip background music unless the user explicitly asks — music under spoken
  podcast audio muddies it.

## Finishing checks
On top of the standard review, ${TOOL_NAMES.imageAsk} the waveform scene and any intro/outro
overlays: the waveform is clearly visible, captions are readable and don't collide
with the waveform or other text, and the FIRST frame of the video shows the
audiogram design — not a plain static waveform placeholder.`;
    },
};
